<template>
  <ViewContainer data-tourid="create-edit-routine">
    <v-container fill-height class="mb-4 px-4 pb-6">
      <v-row class="my-0" v-if="routineDefinitionId">
        <v-col
          cols="auto"
          offset="1"
          sm="12"
          xs="12"
          md="12"
          lg="10"
          offset-sm="0"
          offset-xs="0"
          offset-md="0"
          offset-lg="1"
        >
          <Button
            data-testid="routinesForm-button-allRoutines"
            color="primary"
            plain
            icon="mdi-arrow-left"
            :text="$t('common.button.back')"
            @click="handleGoToAllRoutines"
          />
        </v-col>
      </v-row>
      <v-row v-if="isEditMode && currentStep === STEPS.CHECKLIST">
        <v-col
          cols="auto"
          offset="1"
          sm="12"
          xs="12"
          md="12"
          lg="10"
          offset-sm="0"
          offset-xs="0"
          offset-md="0"
          offset-lg="1"
        >
          <Alert
            class="mb-3"
            textClass="text-body-2"
            :vuetifyProps="{
              color: 'blue',
              icon: 'mdi-alert-circle-outline',
            }"
            :text="$t('checklist.modifying_checklist_problem')"
          />
        </v-col>
      </v-row>
      <v-row class="my-0" style="z-index: 2">
        <v-col
          cols="auto"
          offset="1"
          sm="12"
          xs="12"
          md="12"
          lg="10"
          offset-sm="0"
          offset-xs="0"
          offset-md="0"
          offset-lg="1"
        >
          <RoutineFormNavigation
            data-testid="routineDefinitionFormView-navigation-routineForm"
            :checklistIsValid="checklistIsValid"
            :isLoading="loading"
            :currentStep="currentStep"
            :formIsValid="formIsValid"
            :isEditMode="isEditMode"
            :phases="phases"
            @change="handleStepChange"
          />
        </v-col>
      </v-row>
      <v-row class="mt-0 mb-8" :class="isEditMode ? 'mt-n1' : 'mt-2'">
        <v-col
          cols="auto"
          offset="1"
          sm="12"
          xs="12"
          md="12"
          lg="10"
          offset-sm="0"
          offset-xs="0"
          offset-md="0"
          offset-lg="1"
        >
          <v-form
            v-show="currentStep === STEPS.ROUTINE"
            v-model="formIsValid"
            ref="routineForm"
            lazy-validation
          >
            <FormStep
              ref="routineFormStep"
              :isTemplate="isTemplate"
              :currentStep="currentStep"
              :isEditMode="isEditMode"
              :routineDefinitionId="routineDefinitionId"
              :title="title"
              :form="form"
              :loading="loading"
              :isSaving="isSaving"
              :showAssigneesError="showAssigneesError"
              @handleCancel="handleCancel"
              @handleSave="handleSave"
              @goToChecklistStep="goToChecklistStep"
              @updateForm="updateForm"
            />
          </v-form>
          <Checklist
            data-testid="routineDefinitionFormView-checklist-routineDefinition"
            v-show="currentStep === STEPS.CHECKLIST"
            ref="routineDefinitionChecklistRef"
            :isTemplate="isTemplate"
            :isSaving="isSaving || loading"
            :checklist="checklist"
            :editMode="isEditMode"
            @back="handleBack"
            @next="goToScoringStepFromNext"
            @cancel="handleCancel"
            @save="handleSave('checklist')"
            @skip="handleSkip"
            @change="updateChecklistItems"
            @checklistIsValid="handleChecklistValidityChange"
            @requestDefaultSection="openDefaultSectionDialog"
            @requestDeleteSectionDecision="openDeleteSectionDecisionDialog"
            @requestConditionalRemoval="openConditionalRemovalDialog"
            @requestWarning="openScoringWarningDialog"
          />
          <ScoringStep
            data-testid="routineDefinitionFormView-scoring-routineDefinition"
            v-if="currentStep === STEPS.SCORING"
            :checklist="checklist"
            :isEditMode="isEditMode"
            :isSaving="isSaving"
            :scoringState="scoringState"
            @back="goBackFromScoring"
            @cancel="handleCancel"
            @checklistChange="updateChecklistItems"
            @invalidSave="handleInvalidScoringSave"
            @next="goToConfirmationStep"
            @requestDefaultSection="handleOrphanQuestionRecoveryRequest"
            @save="handleSave('scoring')"
            @scoringTypeChange="updateScoringType"
            @scoringValidationChange="updateScoringValidation"
          />
          <ConfirmationStep
            data-testid="routineDefinitionFormView-confirmation-routineDefinition"
            v-if="currentStep === STEPS.CONFIRMATION"
            :disableConfirm="disableConfirm"
            :isTemplate="isTemplate"
            :isSaving="isSaving"
            :routine="form"
            :checklist="checklist"
            :location="location"
            :showChecklistPreview="checklistHasValue"
            @back="goBackFromConfirmation"
            @confirm="handleConfirm"
          />
        </v-col>
      </v-row>

      <v-dialog v-model="showDiscardChangesDialog" max-width="315" persistent>
        <v-card>
          <v-card-title class="pb-0">
            <span class="h6 grey--text text--darken-4">{{
              $t('common.discard_changes_question')
            }}</span>
          </v-card-title>
          <v-card-text class="mt-4 pb-3">
            <span class="grey--text text--darken-4">
              {{ $t('common.discard_changes_confirmation') }}</span
            >
          </v-card-text>
          <v-card-actions class="pb-3 justify-end">
            <Button
              plain
              color="grey"
              class="me-6"
              data-testid="routineForm-button-cancel"
              :text="$t('common.button.cancel')"
              @click="handleCancelDiscard"
            />
            <Button
              plain
              class="me-5"
              color="danger"
              data-testid="routineForm-button-discard"
              :text="$t('routines.discard')"
              @click="goToPrevRoute"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <ConfirmSkipDialog
        :show="showSkipDialog"
        @cancel="closeSkipDialog"
        @confirm="handleSkipChecklist"
      />
      <DefaultSectionDialog
        data-testid="routineDefinitionFormView-dialog-defaultSection"
        :availableSections="defaultSectionDialog.availableSections"
        :existingSectionName="defaultSectionDialog.existingSectionName"
        :mode="defaultSectionDialog.mode"
        :show="defaultSectionDialog.show"
        @cancel="handleDefaultSectionDialogCancel"
        @confirm="handleCreateDefaultSection"
        @moveToSection="handleMoveQuestionsToSelectedSection"
      />
      <DeleteSectionDecisionDialog
        data-testid="routineDefinitionFormView-dialog-deleteSectionDecision"
        :availableSections="deleteSectionDecisionDialog.availableSections"
        :show="deleteSectionDecisionDialog.show"
        @cancel="handleDeleteSectionDecisionCancel"
        @deleteSectionAndQuestions="handleDeleteSectionAndQuestions"
        @moveSectionQuestions="handleMoveSectionQuestions"
      />
      <RoutineScoringWarningDialog
        data-testid="routineDefinitionFormView-dialog-scoringWarning"
        :actionText="scoringWarningDialog.actionText"
        :show="scoringWarningDialog.show"
        :text="scoringWarningDialog.text"
        :title="scoringWarningDialog.title"
        @close="closeScoringWarningDialog"
      />
      <ConditionalRemovalDialog
        ref="conditionalRemovalDialog"
        data-testid="routineDefinitionFormView-dialog-conditionalRemoval"
        @cancel="handleConditionalRemovalCancel"
        @confirm="handleConditionalRemovalConfirm"
      />
    </v-container>
  </ViewContainer>
</template>

<script>
import _ from 'lodash'
import he from 'he'
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import checklistPermissions from '../../mixins/checklistPermissions.vue'
import getEntityId from '@/lib/getEntityId'

import Alert from '@/components/Alert.vue'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import Checklist from '@/components/Checklists/ChecklistCreation/Checklist.vue'
import { ensureItemIds } from '@/components/Checklists/ChecklistCreation/itemIds'
import ConfirmationStep from './ConfirmationStep.vue'
import ConfirmSkipDialog from './ConfirmSkipDialog.vue'
import Button from '@/components/UI/Button'
import DeleteSectionDecisionDialog from './DeleteSectionDecisionDialog.vue'
import DefaultSectionDialog from './DefaultSectionDialog.vue'
import {
  DEFAULT_SECTION_DIALOG_MODES,
  createDefaultSectionDialogState,
  createDeleteSectionDecisionDialogState,
  createScoringWarningDialogState
} from './dialogState'
import {
  removeInvalidVisibilityRules,
  getDependentItems,
  buildErrorDialogPayload
} from './conditionalLogic/conditionalRemoval'
import getVisibilityRuleErrors from './conditionalLogic/getVisibilityRuleErrors'
import ConditionalRemovalDialog from './conditionalLogic/ConditionalRemovalDialog.vue'
import RoutineFormNavigation from './RoutineFormNavigation.vue'
import RoutineScoringWarningDialog from './RoutineScoringWarningDialog.vue'
import FormStep from './FormStep.vue'
import ScoringStep from './ScoringStep.vue'
import {
  hydrateChecklistScoringForForm,
  mergeChecklistFollowUpScoringState,
  ensureSectionSeparatorForScoring
} from './scoring/scoringViewModel'
import {
  buildRoutineChecklistPayload,
  requiresScoringFollowUpPersist
} from './scoring/scoringPayload'
import {
  getCancelledOrphanQuestionRecoveryResult,
  getMovedOrphanQuestionRecoveryResult,
  getOrphanQuestionRecoveryRequestState,
  getNextOrphanQuestionRecoveryState
} from './scoring/orphanQuestionRecovery'
import {
  findDefaultSection,
  hasOrphanQuestions,
  moveSectionQuestionsToSection,
  moveOrphansIntoDefaultSection,
  getSectionQuestions,
  removeSectionWithQuestions,
  prependDefaultSectionForOrphans
} from './scoring/sectionCompatibility'

const STEPS = {
  ROUTINE: 1,
  CHECKLIST: 2,
  SCORING: 3,
  CONFIRMATION: 4,
}

const DEFAULT_CHECKLIST = Object.freeze({
  isPrivate: true,
  items: [{ type: 'text', name: '' }],
})

const DEFAULT_SECTION_DIALOG_SOURCES = Object.freeze({
  checklist: 'checklist',
  scoring: 'scoring',
})

export default {
  name: 'RoutineDefinitionFormView',

  mixins: [mixin, trackerMixin, checklistPermissions],

  components: {
    Checklist,
    ConditionalRemovalDialog,
    ConfirmSkipDialog,
    DeleteSectionDecisionDialog,
    DefaultSectionDialog,
    Button,
    ViewContainer,
    Alert,
    RoutineFormNavigation,
    RoutineScoringWarningDialog,
    FormStep,
    ConfirmationStep,
    ScoringStep,
  },

  async mounted() {
    await this.fetchRoutineDefinition()
  },

  data() {
    return {
      checklistIsValid: true,
      formIsValid: true,
      rules: [
        val =>
          !!val?.trim() || this.$t('validation.form.this_field_is_required'),
      ],
      STEPS,
      currentStep: STEPS.ROUTINE,
      routine: {},
      form: {
        name: '',
        description: '',
        assignees: [],
        assignedSites: [],
        assignedTeams: [],
        collaborators: [],
        categories: [],
        availableTagsForFindings: [],
        isActive: true,
        isGlobalTemplate: false,
        presetTags: [],
        presetSituationTypes: [],
        requireFindingTags: false,
        isGloballyAssigned: false,
        createdBy: '',
      },
      checklist: globalThis.structuredClone(DEFAULT_CHECKLIST),
      scoringState: {
        scoringType: null,
      },
      scoringValidation: {
        isValid: true,
        generalErrors: [],
        fieldErrors: {},
      },
      originalChecklist: globalThis.structuredClone(DEFAULT_CHECKLIST),
      isSaving: false,
      loading: false,
      prevRoute: null,
      showDiscardChangesDialog: false,
      showSkipDialog: false,
      defaultSectionDialog: createDefaultSectionDialogState(),
      deleteSectionDecisionDialog: createDeleteSectionDecisionDialogState(),
      scoringWarningDialog: createScoringWarningDialogState(),
      showAssigneesError: false,
      confirmationIsDisabled: true,
      checklistIsDisabled: true,
      checklistIsCompleted: false,
      checklistIsSkipped: false,
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  watch: {
    isGlobalRoutine(val) {
      if (val) {
        this.showAssigneesError = false
      } else {
        this.showAssigneesError = !this.hasAssignees
      }
    },

    hasAssignees(hasAssignees) {
      this.showAssigneesError = !hasAssignees
    },

    currentStep: {
      handler(val) {
        if (val === STEPS.CHECKLIST) {
          this.checklistIsDisabled = false
        }
        if (val === STEPS.SCORING || val === STEPS.CONFIRMATION) {
          this.confirmationIsDisabled = false
        }
      },
      immediate: true,
    },
    checklistIsValid: {
      handler(checklistIsValid) {
        if (this.checklistIsSkipped) {
          return (this.checklistIsCompleted = false)
        }
        if (!checklistIsValid) {
          this.checklistIsCompleted = false
        } else {
          this.checklistIsCompleted = true
        }
      },
    },
  },

  computed: {
    ...mapGetters({
      account: 'account',
      accountOrganizationId: 'organizationId',
      siteId: 'siteId',
    }),

    organizationId() {
      // Rever admins can manually set organizationId in the route params for testing purposes
      if (!this.account.isAdmin) return this.accountOrganizationId

      return this.$route?.params?.organizationId || this.accountOrganizationId
    },

    isOrganizationAdminOrReverAdmin() {
      return this.account.isAdmin || this.account.isOrganizationAdmin
    },

    isGlobalRoutine() {
      return this.form.isGloballyAssigned
    },

    hasAssignees() {
      return !(
        _.isEmpty(this.form.assignedSites) &&
        _.isEmpty(this.form.assignees) &&
        _.isEmpty(this.form.assignedTeams)
      )
    },

    checklistChanged() {
      return !_.isEqual(this.originalChecklist?.items, this.checklist?.items)
    },

    checklistHasValue() {
      return this.checklist?.items?.some?.(item => item.name)
    },

    newRoutineHasChanged() {
      return (
        !this.isEditMode &&
        (this.checklistChanged || !_.isEqual(this.form, this.defaultForm))
      )
    },

    isEditMode() {
      return !this.isCopy && Boolean(this.routineDefinitionId)
    },

    isCopy() {
      return this.$route.params.duplicate || this.hasTemplateParam
    },

    isTemplate() {
      return this.form.isGlobalTemplate
    },

    hasTemplateParam() {
      return this.$route.params.template
    },

    routineDefinitionId() {
      return this.$route.params.routineDefinitionId
    },

    title() {
      return this.isEditMode
        ? this.$t('routines.edit_routine_title')
        : this.$t('routines.new_routine')
    },

    siteId() {
      return getEntityId(this.account?.site)
    },

    location() {
      return {
        site: this.account?.site,
      }
    },

    defaultForm() {
      return {
        name: '',
        description: '',
        assignees: [],
        assignedSites: [],
        assignedTeams: [],
        collaborators: [],
        categories: [],
        availableTagsForFindings: [],
        isActive: true,
        isGlobalTemplate: false,
        presetTags: [],
        presetSituationTypes: [],
        requireFindingTags: false,
        isGloballyAssigned: false,
        createdBy: getEntityId(this.account),
      }
    },

    existingRoutineHasChanged() {
      return (
        this.isEditMode &&
        (this.checklistChanged || !_.isEqual(this.form, this.routine))
      )
    },

    formIsCompleted() {
      return (
        this.formIsValid && (this.hasAssignees || this.form.isGloballyAssigned)
      )
    },

    showFormMissingFields() {
      return !this.formIsValid || this.showAssigneesError
    },

    confirmationBackStep() {
      return STEPS.SCORING
    },

    phases() {
      return [
        {
          name: this.$t('routines.routine_step'),
          complete: this.formIsCompleted,
          id: STEPS.ROUTINE,
          missingFields: this.showFormMissingFields,
        },
        {
          name: this.$t('routines.checklist_step'),
          complete: this.checklistIsCompleted,
          disabled: this.checklistIsDisabled,
          id: STEPS.CHECKLIST,
          optional: this.checklistIsValid,
          missingFields: !this.checklistIsValid,
        },
        {
          name: this.$t('routines.scoring.step'),
          complete:
            this.currentStep === STEPS.SCORING &&
            this.scoringValidation?.isValid,
          disabled: this.confirmationIsDisabled,
          id: STEPS.SCORING,
          missingFields: !this.scoringValidation?.isValid,
          errorTitle: this.$t('routines.scoring.invalid_values'),
        },
        {
          name: this.$t('routines.confirmation'),
          complete: this.currentStep === STEPS.CONFIRMATION,
          disabled: this.confirmationIsDisabled,
          id: STEPS.CONFIRMATION,
        },
      ]
    },

    disableConfirm() {
      return (
        !this.checklistIsValid ||
        !this.formIsCompleted ||
        !this.scoringValidation?.isValid
      )
    },
  },

  methods: {
    handleCancelDiscard() {
      this.trackRoutineFormNavigate({ option: 'cancelDiscardChanges' })
      this.showDiscardChangesDialog = false
    },

    handleBack() {
      this.trackRoutineFormNavigate({ option: 'checklistStepBack' })
      this.currentStep = STEPS.ROUTINE
    },

    handleChecklistValidityChange(isValid) {
      this.checklistIsValid = isValid
    },

    goBackFromScoring() {
      this.currentStep = STEPS.CHECKLIST
    },

    goToConfirmationStep() {
      this.currentStep = STEPS.CONFIRMATION
    },

    goBackFromConfirmation() {
      this.currentStep = this.confirmationBackStep
    },

    handleGoToAllRoutines() {
      this.trackRoutineFormNavigate({ option: 'goToAllRoutines' })
      if (this.existingRoutineHasChanged) {
        return (this.showDiscardChangesDialog = true)
      }

      this.goToPrevRoute()
    },

    handleSkip() {
      this.trackRoutineFormNavigate({ option: 'skipChecklist' })
      if (!this.checklistHasValue) return this.handleSkipChecklist()
      this.showSkipDialog = true
    },

    handleSkipChecklist() {
      this.checklist = globalThis.structuredClone(DEFAULT_CHECKLIST)
      this.currentStep = this.STEPS.CONFIRMATION
      this.checklistIsCompleted = false
      this.checklistIsValid = true
      this.checklistIsSkipped = true
      this.showSkipDialog = false
    },

    closeSkipDialog() {
      this.showSkipDialog = false
    },

    goToChecklistStep() {
      this.trackRoutineFormNavigate({ option: 'goToChecklistStep' })
      this.validate()
      this.currentStep = this.STEPS.CHECKLIST
    },

    openDefaultSectionDialog({
      availableSections = [],
      existingSectionName = '',
      items = [],
      mode = null,
      previousItems = [],
      scoringType = null,
      source = DEFAULT_SECTION_DIALOG_SOURCES.checklist,
      forceDefaultSectionDialog = false,
    } = {}) {
      const defaultSection = forceDefaultSectionDialog
        ? null
        : findDefaultSection(items)

      this.defaultSectionDialog = createDefaultSectionDialogState({
        show: true,
        items,
        previousItems,
        scoringType,
        source,
        availableSections,
        mode:
          mode ||
          (defaultSection
            ? DEFAULT_SECTION_DIALOG_MODES.MOVE
            : DEFAULT_SECTION_DIALOG_MODES.CREATE),
        existingSectionName:
          existingSectionName ||
          defaultSection?.name ||
          this.$t('routines.scoring.default_section_name'),
      })
    },

    closeDefaultSectionDialog() {
      this.defaultSectionDialog = createDefaultSectionDialogState()
    },

    openDeleteSectionDecisionDialog({
      sectionIndex = null,
      items = [],
      previousItems = [],
      availableSections = [],
      scoringType = null,
    } = {}) {
      this.deleteSectionDecisionDialog = createDeleteSectionDecisionDialogState(
        {
          show: true,
          sectionIndex,
          items,
          previousItems,
          availableSections,
          scoringType,
        }
      )
    },

    closeDeleteSectionDecisionDialog() {
      this.deleteSectionDecisionDialog =
        createDeleteSectionDecisionDialogState()
    },

    resolveDialogScoringType(scoringType) {
      return scoringType ?? this.checklist?.scoringType ?? null
    },

    handleDefaultSectionDialogCancel() {
      const recoveryResult = getCancelledOrphanQuestionRecoveryResult({
        previousItems: this.defaultSectionDialog.previousItems,
        scoringType: this.resolveDialogScoringType(
          this.defaultSectionDialog.scoringType
        ),
        source: this.defaultSectionDialog.source,
      })

      if (!_.isEmpty(recoveryResult.items)) {
        this.applyWorkflowChecklistOutcome({
          items: recoveryResult.items,
          scoringType: recoveryResult.scoringType,
        })
      }

      if (recoveryResult.returnToChecklist) {
        this.currentStep = this.STEPS.CHECKLIST
      }

      this.closeDefaultSectionDialog()
    },

    handleDeleteSectionDecisionCancel() {
      if (!_.isEmpty(this.deleteSectionDecisionDialog.previousItems)) {
        this.applyWorkflowChecklistOutcome({
          items: this.deleteSectionDecisionDialog.previousItems,
          scoringType: this.resolveDialogScoringType(
            this.deleteSectionDecisionDialog.scoringType
          ),
        })
      }

      this.closeDeleteSectionDecisionDialog()
    },

    handleDeleteSectionAndQuestions() {
      const { items: currentItems, sectionIndex } =
        this.deleteSectionDecisionDialog
      const sectionQuestions = getSectionQuestions(currentItems, sectionIndex)
      const dependentItems = this.isConditionalLogicInRoutinesEnabled()
        ? getDependentItems({
            items: currentItems,
            sourceItems: sectionQuestions,
          })
        : []

      if (!_.isEmpty(dependentItems)) {
        const itemsWithRemovedRules = removeInvalidVisibilityRules({
          items: currentItems,
          itemsWithError: dependentItems,
        })
        const items = removeSectionWithQuestions(
          itemsWithRemovedRules,
          sectionIndex
        )

        // The decision dialog stays open behind the removal dialog: confirming
        // closes both, cancelling keeps it so the admin can pick another action.
        this.openConditionalRemovalDialog({
          items,
          affectedCount: dependentItems.length,
          scoringType: this.resolveDialogScoringType(
            this.deleteSectionDecisionDialog.scoringType
          ),
        })
        return
      }

      const items = removeSectionWithQuestions(currentItems, sectionIndex)

      this.applyWorkflowChecklistOutcome({
        items,
        scoringType: this.resolveDialogScoringType(
          this.deleteSectionDecisionDialog.scoringType
        ),
      })
      this.closeDeleteSectionDecisionDialog()
    },

    handleMoveSectionQuestions(targetSectionId) {
      const { previousItems, scoringType } = this.deleteSectionDecisionDialog
      const items = moveSectionQuestionsToSection(
        this.deleteSectionDecisionDialog.items,
        this.deleteSectionDecisionDialog.sectionIndex,
        targetSectionId
      )

      if (this.isConditionalLogicInRoutinesEnabled()) {
        const itemsWithError = getVisibilityRuleErrors(items)

        if (!_.isEmpty(itemsWithError)) {
          // Landed action: the move stays while the dialog decides; cancel reverts.
          this.applyWorkflowChecklistOutcome({ items })
          this.openConditionalRemovalDialog(
            buildErrorDialogPayload({
              items,
              itemsWithError,
              previousItems,
            })
          )
          return
        }
      }

      this.applyWorkflowChecklistOutcome({
        items,
        scoringType: this.resolveDialogScoringType(scoringType),
      })
      this.closeDeleteSectionDecisionDialog()
    },

    handleMoveQuestionsToSelectedSection(targetSectionId) {
      const recoveryResult = getMovedOrphanQuestionRecoveryResult({
        items: this.defaultSectionDialog.items,
        scoringType: this.resolveDialogScoringType(
          this.defaultSectionDialog.scoringType
        ),
        targetSectionId,
      })
      const previousScoringType = this.checklist?.scoringType ?? null
      const itemsWithError = this.getLandedOrphanRecoveryErrors(
        recoveryResult.items
      )

      if (itemsWithError) {
        // Landed action: the move stays while the dialog decides; cancel
        // restores the pre-recovery items and scoring mode.
        this.applyWorkflowChecklistOutcome({
          items: recoveryResult.items,
          scoringType: recoveryResult.scoringType,
        })
        this.openConditionalRemovalDialog({
          ...buildErrorDialogPayload({
            items: recoveryResult.items,
            itemsWithError,
            previousItems: this.defaultSectionDialog.items,
          }),
          previousScoringType,
        })
        return
      }

      this.applyWorkflowChecklistOutcome({
        items: recoveryResult.items,
        scoringType: recoveryResult.scoringType,
      })
      this.closeDefaultSectionDialog()
    },

    handleOrphanQuestionRecoveryRequest({
      items = [],
      scoringType = null,
      source = DEFAULT_SECTION_DIALOG_SOURCES.checklist,
    } = {}) {
      const recoveryState = getOrphanQuestionRecoveryRequestState({
        items,
        scoringType,
        source,
        isConditionalLogicEnabled: this.isConditionalLogicInRoutinesEnabled(),
        translate: ({ number }) =>
          this.$t('routines.scoring.section_label', {
            number,
          }),
      })

      if (!recoveryState) {
        return
      }

      this.openDefaultSectionDialog(recoveryState.payload)
    },

    tryEnterScoringStep() {
      const recoveryState = getNextOrphanQuestionRecoveryState({
        items: this.checklist?.items,
        scoringType: this.checklist?.scoringType,
        source: DEFAULT_SECTION_DIALOG_SOURCES.scoring,
        isConditionalLogicEnabled: this.isConditionalLogicInRoutinesEnabled(),
        translate: ({ number }) =>
          this.$t('routines.scoring.section_label', {
            number,
          }),
      })

      if (!recoveryState) {
        return false
      }

      this.openDefaultSectionDialog(recoveryState.payload)
      return true
    },

    handleCreateDefaultSection(sectionName) {
      const items = this.resolveDefaultSectionRecoveryItems(sectionName)
      const scoringType = this.resolveDialogScoringType(
        this.defaultSectionDialog.scoringType
      )
      const previousScoringType = this.checklist?.scoringType ?? null
      const itemsWithError = this.getLandedOrphanRecoveryErrors(items)

      if (itemsWithError) {
        // Landed action: the recovery stays while the dialog decides; cancel
        // restores the pre-recovery items and scoring mode.
        this.applyWorkflowChecklistOutcome({ items, scoringType })
        this.openConditionalRemovalDialog({
          ...buildErrorDialogPayload({
            items,
            itemsWithError,
            previousItems: this.defaultSectionDialog.items,
          }),
          previousScoringType,
        })
        return
      }

      this.applyWorkflowChecklistOutcome({ items, scoringType })
      this.closeDefaultSectionDialog()
    },

    // Recovery re-homes root-zone items — under root-zone-only triggers that
    // can break a conditioned section's rule — so all outcomes are gated.
    getLandedOrphanRecoveryErrors(items) {
      if (!this.isConditionalLogicInRoutinesEnabled()) {
        return null
      }

      const itemsWithError = getVisibilityRuleErrors(items)

      return _.isEmpty(itemsWithError) ? null : itemsWithError
    },

    resolveDefaultSectionRecoveryItems(sectionName) {
      const items = this.defaultSectionDialog.items

      // A default section already exists: prepend is a no-op, so the
      // orphans move into it instead — the gate covers any rule the move
      // breaks.
      if (
        this.defaultSectionDialog.mode === DEFAULT_SECTION_DIALOG_MODES.MOVE ||
        (this.isConditionalLogicInRoutinesEnabled() &&
          findDefaultSection(items))
      ) {
        return moveOrphansIntoDefaultSection(items)
      }

      return prependDefaultSectionForOrphans(items, sectionName)
    },

    openScoringWarningDialog({ titleKey, textKey, actionTextKey } = {}) {
      this.scoringWarningDialog = createScoringWarningDialogState({
        show: true,
        title: this.$t(titleKey || 'common.attention'),
        text: this.$t(textKey || 'common.button.confirm_default_description'),
        actionText: this.$t(actionTextKey || 'common.button.ok'),
      })
    },

    closeScoringWarningDialog() {
      this.scoringWarningDialog = createScoringWarningDialogState()
    },

    openConditionalRemovalDialog(payload) {
      this.$refs.conditionalRemovalDialog.open(payload)
    },

    handleConditionalRemovalConfirm({ items, scoringType, shouldApply }) {
      // shouldApply is the empty-payload backstop; an unset scoringType
      // leaves the current one untouched.
      if (shouldApply) {
        this.applyWorkflowChecklistOutcome({ items, scoringType })
      }

      this.closeDeleteSectionDecisionDialog()
      this.closeDefaultSectionDialog()
    },

    handleConditionalRemovalCancel({
      previousItems,
      previousScoringType,
    } = {}) {
      if (!_.isEmpty(previousItems)) {
        this.applyWorkflowChecklistOutcome({
          items: previousItems,
          scoringType: previousScoringType,
        })
      }
    },

    goToScoringStepFromNext(items = null) {
      this.trackRoutineFormNavigate({ option: 'goToScoringStep' })
      this.validateChecklist()
      this.checklistIsCompleted = this.checklistHasValue

      if (_.isArray(items)) {
        this.applyWorkflowChecklistOutcome({
          items,
          scoringType: this.resolveCurrentScoringType(),
        })
      }

      if (this.tryEnterScoringStep()) {
        return
      }

      this.syncChecklistWithScoring()

      this.currentStep = this.STEPS.SCORING
    },

    findFirstInvalidField() {
      const routineFormStep = this.$refs.routineFormStep

      for (const ref in routineFormStep.$refs) {
        const field = routineFormStep.$refs[ref]

        if (field && !field.valid) {
          return field
        }
      }

      return null
    },

    scrollToField(field) {
      if (!field) return
      const element = field?.$el ? field.$el : field

      field.focus()
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },

    // User-driven checklist/scoring edits should normalize section scoring state.
    updateChecklistItems(payload) {
      if (_.isArray(payload)) {
        this.applyUserChecklistEdit({
          items: payload,
          scoringType: this.resolveCurrentScoringType(),
        })
        return
      }

      this.applyUserChecklistEdit({
        items: payload?.items,
        scoringType: payload?.scoringType,
      })
    },

    // Array payloads carry no scoringType of their own — keep the current one.
    resolveCurrentScoringType() {
      return (
        this.checklist?.scoringType ?? this.scoringState?.scoringType ?? null
      )
    },

    // Dialog/workflow transitions preserve the exact resulting checklist state.
    applyWorkflowChecklistOutcome({ items, scoringType } = {}) {
      this.setChecklistState({
        items,
        scoringType,
        normalizeForSectionScoring: false,
      })
    },

    // Shared checklist write path: both intents land here, so the checklist
    // state and its derived flags are updated in exactly one place.
    setChecklistState({
      items,
      scoringType,
      normalizeForSectionScoring = false,
    } = {}) {
      setTimeout(() => this.validateChecklist(), 500)

      let checklistUpdate = {
        ...this.checklist,
        items,
      }

      if (scoringType !== undefined) {
        checklistUpdate = {
          ...checklistUpdate,
          scoringType,
        }
      }

      if (normalizeForSectionScoring) {
        checklistUpdate =
          this.normalizeChecklistForSectionScoring(checklistUpdate)
      }

      this.checklist = checklistUpdate

      if (_.isEmpty(this.checklist.items)) {
        this.checklistIsCompleted = false
      }

      if (scoringType !== undefined) {
        this.setScoringType(scoringType)
      }
    },

    // User-driven checklist/scoring edits should normalize section scoring state.
    applyUserChecklistEdit({ items, scoringType } = {}) {
      this.setChecklistState({
        items,
        scoringType,
        normalizeForSectionScoring: true,
      })
    },

    setScoringType(scoringType) {
      this.checklist = {
        ...this.checklist,
        scoringType,
      }

      this.scoringState = {
        ...this.scoringState,
        scoringType,
      }
    },

    updateScoringType(scoringType) {
      this.setScoringType(scoringType)
    },

    normalizeChecklistForSectionScoring(checklist) {
      if (checklist?.scoringType !== 'section') {
        return checklist
      }

      if (hasOrphanQuestions(checklist?.items)) {
        return checklist
      }

      const { items } = ensureSectionSeparatorForScoring(
        checklist?.items,
        this.$t('routines.scoring.default_section_name')
      )

      return {
        ...checklist,
        items,
      }
    },

    syncChecklistWithScoring() {
      this.checklist = this.normalizeChecklistForSectionScoring(this.checklist)
    },

    updateScoringValidation(validationState) {
      this.scoringValidation = validationState
    },

    handleInvalidScoringSave() {
      return this.showError(this.$t('routines.scoring.invalid_values'))
    },

    handleStepChange(destinationStep) {
      if (destinationStep === STEPS.SCORING && this.tryEnterScoringStep()) {
        return
      }

      this.trackRoutineFormNavigate({
        option: `stepChangedTo${destinationStep}`,
      })
      this.currentStep = destinationStep
    },

    validate() {
      this.showAssigneesError = !this.hasAssignees && !this.isGlobalRoutine

      return this.$refs.routineForm.validate()
    },

    validateChecklist() {
      this.checklistIsValid =
        this.$refs.routineDefinitionChecklistRef?.validate()
    },

    getValidationErrorMessage() {
      if (this.scoringValidation && !this.scoringValidation.isValid) {
        return this.$t('routines.scoring.invalid_values')
      }

      if (!this.formIsValid || !this.checklistIsValid) {
        return this.$t('routines.missing_fields')
      }

      return null
    },

    getRoutineName(originalName) {
      const maxLength = this.$constants.routines.definition.titleMaxLength
      const copy = ` ${this.$t('boards.copy')}`

      const availableLength = maxLength - copy.length

      const trimmedName = originalName.substring(0, availableLength)

      return trimmedName + copy
    },

    async fetchRoutineDefinition() {
      this.form = { ...this.form, createdBy: getEntityId(this.account) }
      if (!this.routineDefinitionId) return

      try {
        this.loading = true

        const { data: routineDefinition } = await api.routine.getById(
          this.organizationId,
          this.routineDefinitionId
        )

        this.routine = { ...routineDefinition }
        this.routine = {
          ..._.omit(routineDefinition, ['_v', '_id']),
          assignees:
            routineDefinition.isGloballyAssigned &&
            !this.isOrganizationAdminOrReverAdmin
              ? []
              : (routineDefinition?.assignees?.map?.(assignee =>
                  getEntityId(assignee)
                ) ?? []),
          collaborators:
            routineDefinition?.collaborators?.map?.(collaborator =>
              getEntityId(collaborator)
            ) ?? [],
          isGlobalTemplate: this.isEditMode
            ? routineDefinition.isGlobalTemplate
            : false,
          assignedSites:
            routineDefinition.isGloballyAssigned &&
            !this.isOrganizationAdminOrReverAdmin
              ? []
              : (routineDefinition?.assignedSites?.map?.(site =>
                  getEntityId(site)
                ) ?? []),
          assignedTeams:
            routineDefinition.isGloballyAssigned &&
            !this.isOrganizationAdminOrReverAdmin
              ? []
              : (routineDefinition?.assignedTeams?.map?.(team =>
                  getEntityId(team)
                ) ?? []),
          id: this.isCopy ? null : this.routine.id,
          isGloballyAssigned: this.isOrganizationAdminOrReverAdmin
            ? routineDefinition.isGloballyAssigned
            : false,
          createdBy: getEntityId(routineDefinition?.createdBy),
          availableTagsForFindings:
            routineDefinition?.availableTagsForFindings?.map?.(getEntityId) ??
            [],
          name: this.isCopy
            ? this.getRoutineName(routineDefinition.name)
            : routineDefinition.name,
          requireFindingTags: Boolean(routineDefinition?.requireFindingTags),
        }

        if (this.hasTemplateParam) {
          this.routine.assignees = []
          this.routine.assignedTeams = []
          this.routine.assignedSites = []
          this.routine.collaborators = []
          //Categories, presetSituationTypes and presetTags must be cleared because some values can not be present in the site of the routine that is being created.
          this.routine.categories = []
          this.routine.presetSituationTypes = []
          this.routine.presetTags = []
          this.routine.availableTagsForFindings = []
        }

        if (this.routine.checklist) {
          await this.fetchChecklist(this.routine.checklist)
        }

        this.form = _.clone(this.routine)
      } catch (err) {
        this.checkNotFoundError(err)
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    isConditionalLogicInRoutinesEnabled() {
      return this.$isFeatureEnabled(
        this.$featuresKeys?.conditionalLogicInRoutines
      )
    },

    async fetchChecklist(checklistId) {
      try {
        this.loading = true

        const { data } = await api.routine.getChecklistById(
          this.organizationId,
          checklistId
        )

        const checklist = hydrateChecklistScoringForForm({ ...data })

        await this.$onFeatureFlagsReady()

        if (this.isConditionalLogicInRoutinesEnabled()) {
          checklist.items = ensureItemIds(checklist.items)
        }

        const copiedChecklist = this.isCopy
          ? _.omit(checklist, ['id', '_id'])
          : checklist

        this.checklist = _.clone(copiedChecklist)
        this.originalChecklist = Object.freeze(_.clone(copiedChecklist))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async createRoutine(data) {
      const response = await api.routine.create(this.organizationId, data)
      this.routine = response.data
    },

    async updateRoutine(routineId) {
      const routine = {
        ...this.form,
        availableTagsForFindings:
          this.form?.availableTagsForFindings?.map?.(getEntityId) ?? [],
        country: this.form?.countryCode,
        presetTags: this.form.presetTags?.map?.(getEntityId) ?? [],
        site: getEntityId(this.form?.site),
      }

      if (routine.isGloballyAssigned) {
        routine.assignees = []
        routine.assignedSites = []
        routine.assignedTeams = []
      }

      return api.routine.update(this.organizationId, routineId, {
        ..._.omit(routine, ['_v', '_id']),
      })
    },

    async updateChecklist(
      updateDuplicated = false,
      checklistOverride = null,
      options = {}
    ) {
      const {
        omitUnsavedOptionScoring = false,
        fallbackChecklist = null,
        preserveFallbackScoring = false,
      } = options
      const checklist = checklistOverride || this.checklist
      const checklistId = getEntityId(checklist)

      await this.$onFeatureFlagsReady()
      const isConditionalLogicEnabled = this.$isFeatureEnabled(
        this.$featuresKeys?.conditionalLogicInRoutines
      )

      const checklistPayload = buildRoutineChecklistPayload({
        checklist,
        isConditionalLogicEnabled,
        routineName: this.form.name,
        translate: (key, params) => he.decode(this.$t(key, params)),
        isPrivate: checklist.isPrivate,
        site: updateDuplicated ? this.siteId : null,
        entities: updateDuplicated
          ? [this.$constants.checklist.availableEntities.routine]
          : null,
        omitUnsavedOptionScoring,
        fallbackChecklist,
        preserveFallbackScoring,
      })

      const response = api.routine.updateChecklist(
        this.organizationId,
        checklistId,
        checklistPayload
      )
      if (this.currentStep === STEPS.CHECKLIST) {
        this.currentStep = this.STEPS.CHECKLIST
      }
      return response
    },

    async createChecklist({ omitUnsavedOptionScoring = false } = {}) {
      await this.$onFeatureFlagsReady()
      const isConditionalLogicEnabled = this.$isFeatureEnabled(
        this.$featuresKeys?.conditionalLogicInRoutines
      )

      return api.routine.createChecklist(
        this.organizationId,
        buildRoutineChecklistPayload({
          checklist: this.checklist,
          isConditionalLogicEnabled,
          routineName: this.form.name,
          translate: (key, params) => he.decode(this.$t(key, params)),
          isPrivate: this.checklist.isPrivate,
          site: this.siteId,
          entities: [this.$constants.checklist.availableEntities.routine],
          omitUnsavedOptionScoring,
        })
      )
    },

    async persistChecklistThenRestoreScoring(persistChecklist) {
      const sourceChecklist = globalThis.structuredClone(this.checklist)
      const initialResponse = await persistChecklist(sourceChecklist)
      const mergedChecklist = mergeChecklistFollowUpScoringState(
        initialResponse.data,
        sourceChecklist
      )

      this.checklist = mergedChecklist

      const persistedChecklistResponse = await this.updateChecklist(
        false,
        mergedChecklist
      )
      this.checklist = hydrateChecklistScoringForForm(
        persistedChecklistResponse.data
      )

      return persistedChecklistResponse
    },

    async persistNewChecklistWithOptionScoring() {
      return this.persistChecklistThenRestoreScoring(() =>
        this.createChecklist({
          omitUnsavedOptionScoring: true,
        })
      )
    },

    async persistExistingChecklistWithOptionScoring() {
      return this.persistChecklistThenRestoreScoring(sourceChecklist =>
        this.updateChecklist(false, sourceChecklist, {
          omitUnsavedOptionScoring: true,
          fallbackChecklist: this.originalChecklist,
          preserveFallbackScoring: true,
        })
      )
    },

    isUpdatingCurrentChecklist() {
      return this.routine.checklist === getEntityId(this.checklist)
    },

    requiresChecklistFollowUpUpdate() {
      return requiresScoringFollowUpPersist(this.checklist)
    },

    async saveExistingChecklistDefinition() {
      if (this.requiresChecklistFollowUpUpdate()) {
        return this.persistExistingChecklistWithOptionScoring()
      }

      return this.updateChecklist()
    },

    async saveNewChecklistDefinition() {
      if (this.requiresChecklistFollowUpUpdate()) {
        return this.persistNewChecklistWithOptionScoring()
      }

      return this.createChecklist()
    },

    async saveChecklistDefinition() {
      if (this.isUpdatingCurrentChecklist()) {
        return this.saveExistingChecklistDefinition()
      }

      return this.saveNewChecklistDefinition()
    },

    async handleSave(from) {
      const trackingData = {
        name: this.form.name,
        isActive: this.form.isActive,
        hasDescription: Boolean(this.form.description),
        hasCustomAssignees: this.hasAssignees,
        isGlobalRoutine: this.isGlobalRoutine,
        numberOfAssignedUsers: this.form?.assignees?.length ?? 0,
        numberOfAssignedTeams: this.form?.assignedTeams?.length ?? 0,
        numberOfAssignedSites: this.form?.assignedSites?.length ?? 0,
        hasCollaborators: Boolean(this.form?.collaborators?.length),
        hasRoutineType: Boolean(this.form?.type),
        hasCategories: Boolean(this.form?.categories?.length),
        frequency: this.form?.frequency ?? '',
        isDuplicate: Boolean(this.$route.params.duplicate),
        isTemplate: Boolean(this.isTemplate),
      }
      try {
        this.formIsValid = this.validate() && !this.showAssigneesError
        const validationErrorMessage = this.getValidationErrorMessage()

        if (validationErrorMessage) {
          return this.showError(validationErrorMessage)
        }

        this.isSaving = true

        if (this.checklistHasValue) {
          const checklistResponse = await this.saveChecklistDefinition()
          this.checklist = hydrateChecklistScoringForForm(
            checklistResponse.data
          )
          this.form.checklist = getEntityId(this.checklist)
        }

        const routineResponse = await this.updateRoutine(
          this.routineDefinitionId
        )
        this.routine = routineResponse.data

        this.showSuccess(this.$t('routines.routine_updated_successfully'))

        this.trackRoutineUpdate({
          ...trackingData,
          hasChecklist: this.checklistHasValue,
          status: 'success',
          savedFrom: from,
        })
        this.fetchRoutineDefinition()
      } catch (err) {
        this.handleRequestError(err)
        this.trackRoutineUpdate({
          ...trackingData,
          hasChecklist: this.checklistHasValue,
          status: 'fail',
          savedFrom: from,
        })
      } finally {
        this.isSaving = false
      }
    },

    async handleConfirm() {
      const trackingData = {
        name: this.form.name,
        isActive: this.form.isActive,
        hasDescription: Boolean(this.form.description),
        hasAssignees: this.hasAssignees,
        // numberOfAssignees: this.form?.assignees?.length ?? 0, // TODO check mixpanel data
        hasRoutineType: Boolean(this.form?.type),
        hasCategories: Boolean(this.form?.categories?.length),
        frequency: this.form?.frequency ?? '',
        isDuplicate: Boolean(this.$route.params.duplicate),
        isTemplate: Boolean(this.isTemplate),
        isFromTemplate: Boolean(this.hasTemplateParam),
      }

      const routine = {
        ...this.form,
        availableTagsForFindings:
          this.form.availableTagsForFindings?.map?.(getEntityId) ?? [],
        presetTags: this.form.presetTags?.map?.(getEntityId) ?? [],
        requireFindingTags: Boolean(this.form?.requireFindingTags),
        site: getEntityId(this.location?.site),
      }

      if (routine.isGloballyAssigned) {
        routine.assignees = []
        routine.assignedSites = []
        routine.assignedTeams = []
      }

      try {
        this.isSaving = true

        const checklistId = await this.getChecklistId()
        routine.checklist = checklistId

        await this.createRoutine(routine)

        this.trackRoutineCreation({
          ...trackingData,
          hasChecklist: this.checklistHasValue,
          status: 'success',
        })

        this.navigateTo({
          name: 'routineDetail',
          params: { routineId: this.routine.id },
        })
        this.showSuccess(this.$t('routines.routine_created_successfully'))
      } catch (err) {
        this.handleRequestError(err)
        this.trackRoutineCreation({
          ...trackingData,
          hasChecklist: this.checklistHasValue,
          status: 'fail',
        })
      } finally {
        this.isSaving = false
      }
    },

    async getChecklistId() {
      const shouldCreateNewChecklist = this.checklistHasValue
      const shouldCreateDuplicateFromRoutineTemplateChecklist =
        this.isCopy && this.checklistHasValue

      if (
        shouldCreateNewChecklist ||
        shouldCreateDuplicateFromRoutineTemplateChecklist
      ) {
        const response = await this.saveChecklistDefinition()
        return getEntityId(response.data)
      }

      return null
    },

    goToPrevRoute() {
      this.trackRoutineFormNavigate({ option: 'backToAllRoutines' })
      if (this.prevRoute) this.replaceRoute(this.prevRoute?.fullPath)
      else this.$router.back()
    },

    handleCancel() {
      this.trackRoutineFormNavigate({ option: 'cancelSave' })
      if (this.newRoutineHasChanged) {
        return (this.showDiscardChangesDialog = true)
      }
      if (this.existingRoutineHasChanged) {
        return (this.showDiscardChangesDialog = true)
      }

      this.goToPrevRoute()
    },

    updateForm(data, field) {
      this.form[field] = data
    },
  },
}
</script>
